/**
 * "What name should buyers see?" — for a signed-in seller with no first name.
 *
 * Presentational: the save lives in `useNameCapture`. Borrows the email capture's
 * panel styles (styles/components/email-capture.scss) so the two asks read as one
 * family when both show on the same page.
 */
import type { useNameCapture } from './useNameCapture';
import shared from '../../data/js/shared';

export interface NameCaptureProps {
  capture: ReturnType<typeof useNameCapture>;
  /** Surface-specific framing. Defaults to the account wording. */
  heading?: string;
  subheading?: string;
}

type NameCaptureCopy = (typeof shared)['nameCapture'];

const messagesFor = (copy: NameCaptureCopy): Record<string, string> => ({
  name_required: copy.nameRequired,
  save_failed: copy.saveFailed,
  network_error: copy.networkError,
});

export default function NameCapture({ capture, heading, subheading }: NameCaptureProps) {
  const copy = shared.nameCapture;
  const { stage, name, setName, savedFirstName, error, busy } = capture;

  if (stage === 'saved') {
    return (
      <div className="email-capture-section name-capture-section" data-testid="name-capture-saved">
        <p className="email-capture-confirmed">{`${copy.saved} ${savedFirstName}`}</p>
      </div>
    );
  }

  const message = error ? (messagesFor(copy)[error] ?? copy.saveFailed) : null;

  return (
    <form
      className="email-capture-section name-capture-section"
      data-testid="name-capture"
      onSubmit={(e) => {
        e.preventDefault();
        void capture.save();
      }}
    >
      <h3 className="email-capture-heading">{heading ?? copy.heading}</h3>
      <p className="email-capture-subheading">{subheading ?? copy.subheading}</p>

      <div className="email-capture-controls">
        <input
          type="text"
          autoComplete="name"
          className="email-capture-input"
          aria-label={copy.nameLabel}
          placeholder={copy.nameLabel}
          data-testid="name-capture-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="submit"
          className="email-capture-button"
          data-testid="name-capture-save"
          disabled={busy}
        >
          {busy ? copy.saving : copy.save}
        </button>
      </div>

      {message && (
        <p className="email-capture-error" role="alert" data-testid="name-capture-error">
          {message}
        </p>
      )}
    </form>
  );
}
